import { useState } from "react";
import { useAuth } from "../../context";
import useFetchObjects from "../../api/useFetchObjects";
import useDebounce from "../../utils/useDebounce";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { Person as PersonIcon } from "@mui/icons-material";

const DEBOUNCE_MS = 400;
const MIN_CHARS = 2;

/**
 * PatientSearch Component
 * Reusable component for searching patients by name/phone across all modules.
 * Uses debounced search (400ms) to reduce API calls.
 *
 * Usage:
 * <PatientSearch
 *   value={selectedPatient}
 *   onChange={(patient) => setSelectedPatient(patient)}
 *   placeholder="Search patient by name or phone..."
 * />
 */
export default function PatientSearch({
  value,
  onChange,
  placeholder = "Search patient by name or phone...",
  label = "Patient",
  fullWidth = true,
  size = "medium",
  required = false,
  error = false,
  helperText,
  pageSize = 25,
}) {
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [inputValue, setInputValue] = useState("");

  const debouncedSearch = useDebounce(inputValue.trim(), DEBOUNCE_MS);
  const shouldFetch = debouncedSearch.length >= MIN_CHARS;

  const { data: patientsResponse, isLoading } = useFetchObjects(
    shouldFetch ? ["patients", "search", debouncedSearch] : null,
    shouldFetch
      ? `patients/?search=${encodeURIComponent(debouncedSearch)}&page_size=${pageSize}`
      : "",
    token,
    { enabled: shouldFetch }
  );

  const patients = Array.isArray(patientsResponse)
    ? patientsResponse
    : patientsResponse?.results ?? [];

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      options={patients}
      getOptionLabel={(option) => {
        if (!option) return "";
        const name = `${option.firstname || ""} ${option.lastname || ""}`.trim();
        const phone = option.phone ? ` · ${option.phone}` : "";
        return name || option.phone || "";
      }}
      value={value}
      onChange={(e, newValue) => onChange(newValue)}
      onInputChange={(e, newInputValue) => setInputValue(newInputValue)}
      inputValue={inputValue}
      loading={isLoading}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body1" fontWeight="bold">
              {option.firstname} {option.lastname || ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {option.phone || "No phone"}
              {option.father_name ? ` · ${option.father_name}` : ""}
            </Typography>
          </Box>
        </Box>
      )}
      noOptionsText={
        inputValue.length < MIN_CHARS
          ? `Type at least ${MIN_CHARS} characters to search`
          : "No patients found"
      }
    />
  );
}
